import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Truck, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Row {
  vehicle_id: string;
  plate: string | null;
  code: string | null;
  brand: string | null;
  model: string | null;
  usage_role: string | null;
  revenue_30d: string;
  distance_30d: string;
  trips_30d: number;
  fuel_cost_30d: string;
  maint_cost_30d: string;
  waste_cost_30d: string;
  net_profit_30d: string;
  cost_per_km_30d: string | null;
  margin_pct_30d: string | null;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function tierColor(margin: number | null): string {
  if (margin === null) return 'border-border bg-card';
  if (margin > 30) return 'border-emerald-700/40 bg-emerald-700/10';
  if (margin > 20) return 'border-emerald-500/40 bg-emerald-500/10';
  if (margin > 10) return 'border-amber-500/40 bg-amber-500/10';
  if (margin > 0)  return 'border-orange-500/40 bg-orange-500/10';
  return 'border-rose-500/40 bg-rose-500/10';
}

function tierLabel(margin: number | null): string {
  if (margin === null) return 'No data';
  if (margin > 30) return 'Excellent';
  if (margin > 20) return 'Good';
  if (margin > 10) return 'Marginal';
  if (margin > 0)  return 'Tight';
  return 'Loss';
}

export default function VehicleHeatmapPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await apiClient.get<Row[]>('/api/v1/analytics/vehicle-financial-summary');
      setRows(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Truck className="h-4 w-4" /> Vehicle profitability — last 30d</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No active vehicles.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((r) => {
              const margin = r.margin_pct_30d !== null ? Number(r.margin_pct_30d) : null;
              const net = Number(r.net_profit_30d);
              return (
                <div key={r.vehicle_id} className={'rounded-lg border p-4 ' + tierColor(margin)}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-mono font-medium">{r.plate ?? r.code ?? r.vehicle_id.slice(0, 8)}</div>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-background/40 border border-border">{tierLabel(margin)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{r.brand} {r.model}</div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div><div className="text-muted-foreground">Trips</div><div className="font-mono">{r.trips_30d}</div></div>
                    <div><div className="text-muted-foreground">Distance</div><div className="font-mono">{Number(r.distance_30d).toFixed(0)} km</div></div>
                    <div><div className="text-muted-foreground">Revenue</div><div className="font-mono">{fmtSar(Number(r.revenue_30d))}</div></div>
                    <div><div className="text-muted-foreground">Fuel</div><div className="font-mono">−{fmtSar(Number(r.fuel_cost_30d))}</div></div>
                    <div><div className="text-muted-foreground">Maint</div><div className="font-mono">−{fmtSar(Number(r.maint_cost_30d))}</div></div>
                    <div><div className="text-muted-foreground">Waste</div><div className="font-mono text-amber-700 dark:text-amber-400">−{fmtSar(Number(r.waste_cost_30d))}</div></div>
                  </div>
                  <div className="border-t border-border my-2" />
                  <div className="flex items-baseline justify-between">
                    <div className="text-xs text-muted-foreground">Net 30d</div>
                    <div className={'font-mono font-medium ' + (net >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400')}>
                      {net >= 0 ? '' : ''}{fmtSar(net)}
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between mt-1">
                    <div className="text-xs text-muted-foreground">Margin</div>
                    <div className="font-mono">{margin !== null ? margin.toFixed(0) + '%' : '—'}</div>
                  </div>
                  {r.cost_per_km_30d !== null && (
                    <div className="flex items-baseline justify-between mt-1">
                      <div className="text-xs text-muted-foreground">Cost/km</div>
                      <div className="font-mono">{Number(r.cost_per_km_30d).toFixed(2)}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}


